// App.tsx — sceglie cosa mostrare: avvio, manutenzione, aggiornamento richiesto, Home o pannello /admin.
import { RiesciANonRidere } from "./social/RiesciANonRidere.tsx";
import { lazy, Suspense, useCallback, useEffect, useMemo, useState, type ReactNode } from "react";
import { useTranslation } from "react-i18next";
import { AvvisoRete } from "./componenti/AvvisoRete.tsx";
import { Home } from "./schermate/Home.tsx";
import { Gioco } from "./schermate/Gioco.tsx";
import { Accesso } from "./account/Accesso.tsx";
import { Profilo } from "./account/Profilo.tsx";
import { Liberatoria } from "./account/Liberatoria.tsx";
import { Piani } from "./account/Piani.tsx";
import { SceltaAvatar } from "./account/SceltaAvatar.tsx";
import { Eta, etaAnni } from "./account/Eta.tsx";
import { ProfiloPubblico } from "./social/ProfiloPubblico.tsx";
import { Negozio } from "./social/Negozio.tsx";
import { Aiuto } from "./aiuto/Aiuto.tsx";
import { Classifica } from "./social/Classifica.tsx";
import { Amici } from "./social/Amici.tsx";
import { Disponibili } from "./social/Disponibili.tsx";
import { Richieste } from "./social/Richieste.tsx";
import { Torneo } from "./torneo/Torneo.tsx";
import { Guarda } from "./torneo/Guarda.tsx";
import { Gruppo } from "./gruppo/Gruppo.tsx";
import { StrumentiProva } from "./prova/StrumentiProva.tsx";
import { torneoDellaPartita } from "./servizi/tornei.ts";
import { aggiornaIo, useAccount } from "./servizi/account.ts";
import { leggiPreferenza } from "./servizi/preferenze.ts";
import { usePercorso, vai } from "./servizi/percorso.ts";
import { Schermata } from "./schermate/Schermata.tsx";
import { useTema } from "./temi/useTema.ts";
import { TEMA_RIPIEGO, TEMI_GOLD_PREDEFINITI, TEMI_GRATUITI, temaUsabile, eIdTema, type IdTema } from "./temi/temi.ts";
import {
  caricaImpostazioni,
  eBooleano,
  eElencoTesti,
  eNumero,
  eTesto,
  leggiImpostazione,
  useImpostazioni,
} from "./servizi/impostazioni.ts";
import { useTempoReale } from "./servizi/tempoReale.ts";
import { salvaPreferenza } from "./servizi/preferenze.ts";
import { cambiaLingua } from "./i18n/index.ts";
import { normalizzaLingua, LINGUE_SUPPORTATE } from "../../condiviso/lingue.ts";
import {
  IMP_APP,
  PERCORSO_ACCESSO,
  PERCORSO_ADMIN,
  PERCORSO_CLASSIFICA,
  PERCORSO_AMICI,
  PERCORSO_DISPONIBILI,
  PERCORSO_AIUTO,
  PERCORSO_AVATAR,
  PERCORSO_NEGOZIO,
  PERCORSO_PIANI,
  PERCORSO_PROFILO,
  PERCORSO_PROFILO_PUBBLICO,
  PERCORSO_VIDEO,
  PERCORSO_SFIDA,
  PERCORSO_TORNEO,
  PERCORSO_GRUPPO,
  PERCORSO_PROVA,
  PREFERENZE,
  VERSIONE_APP, PERCORSO_LIBERATORIA } from "./costanti.ts";
import { CODICE_REGEX } from "../../condiviso/protocollo.ts";

/** «Nuova sfida» a fine partita: si torna alla home con la finestra già aperta. */
const PARAMETRO_NUOVA = "nuova";

const Admin = lazy(() => import("./admin/Admin.tsx"));

export function App() {
  const { t, i18n } = useTranslation();
  const server = useImpostazioni();
  const tempoReale = useTempoReale();

  // Le impostazioni cambiano riferimento a ogni lettura: si ricalcolano solo quando arrivano valori nuovi.
  const percorso = usePercorso();
  const { temi, temiGratuiti, temaPredefinito, lingue, manutenzione, versioneMinima, giocoAttivo, etaMinima, accountAttivi } = useMemo(
    () => ({
      temi: leggiImpostazione<string[]>(IMP_APP.temiDisponibili, ["laughdown"], eElencoTesti),
      temiGratuiti: leggiImpostazione<string[]>(IMP_APP.temiGratuiti, [...TEMI_GRATUITI], eElencoTesti),
      temaPredefinito: leggiImpostazione(IMP_APP.temaPredefinito, TEMA_RIPIEGO as string, eTesto),
      lingue: leggiImpostazione<string[]>(IMP_APP.lingueAttive, [...LINGUE_SUPPORTATE], eElencoTesti),
      manutenzione: leggiImpostazione(IMP_APP.manutenzione, false, eBooleano),
      versioneMinima: leggiImpostazione(IMP_APP.versioneMinima, 1, eNumero),
      giocoAttivo: leggiImpostazione(IMP_APP.giocoAttivo, true, eBooleano),
      etaMinima: leggiImpostazione(IMP_APP.etaMinima, 16, eNumero),
      accountAttivi: leggiImpostazione(IMP_APP.accountAttivi, true, eBooleano),
    }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [server.valori],
  );

  // Temi Silver: si vedono tutti nel pennello, si usano con Silver/Gold. Finché l'account si sta caricando non si
  // toglie nulla (niente lampo del tema predefinito a chi è abbonato).
  const account = useAccount();
  const pianoUtente = account.tipo === "registrato" ? account.io.piano : "base";
  const temiGold = leggiImpostazione<string[]>(IMP_APP.temiGold, [...TEMI_GOLD_PREDEFINITI], eElencoTesti);
  const goldInVendita = leggiImpostazione(IMP_APP.goldInVendita, false, eBooleano);
  // Temi Gold nel pennello solo se Gold si può comprare (o se lo si ha già): altrimenti «si sblocca con Gold» non porta a nulla.
  const temiVisibili = useMemo(
    () => (goldInVendita || pianoUtente === "gold" ? temi : temi.filter((id) => !temiGold.includes(id))),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [temi, goldInVendita, pianoUtente, JSON.stringify(temiGold)],
  );
  const temiUsabili = useMemo(
    () => (account.tipo === "caricamento" ? temiVisibili : temiVisibili.filter((id) => eIdTema(id) && temaUsabile(id as IdTema, pianoUtente, temiGratuiti, temiGold))),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [temiVisibili, temiGratuiti, pianoUtente, account.tipo, JSON.stringify(temiGold)],
  );
  const tema = useTema(temaPredefinito, temiUsabili);
  // Età dichiarata alla prima apertura (sul telefono): sotto l'età minima l'app non si usa.
  const [nascita, setNascita] = useState(() => leggiPreferenza(PREFERENZE.nascita));
  const eta = nascita ? etaAnni(nascita, new Date().toISOString().slice(0, 10)) : null;

  // Chi ha una sessione salvata: profilo, piano e quota all'avvio.
  useEffect(() => {
    if (server.stato === "pronte") void aggiornaIo().catch(() => undefined);
  }, [server.stato]);
  const lingua = i18n.language; // useTranslation ridisegna quando cambia

  const scegliLingua = useCallback(async (l: string) => {
    const n = normalizzaLingua(l);
    if (!n) return;
    await cambiaLingua(n);
    salvaPreferenza(PREFERENZE.lingua, n);
  }, []);

  if (percorso.startsWith(PERCORSO_ADMIN)) {
    return (
      <Suspense fallback={<Schermata tema={tema.tema} testo={t("generale.caricamento")} />}>
        <Admin />
      </Suspense>
    );
  }

  if (server.stato === "caricamento") return <Schermata tema={tema.tema} testo={t("generale.caricamento")} />;

  if (server.stato === "pronte" && manutenzione) return <Schermata tema={tema.tema} testo={t("rete.manutenzione")} />;

  if (server.stato === "pronte" && Number(VERSIONE_APP) < versioneMinima) {
    return <Schermata tema={tema.tema} testo={t("rete.aggiorna")} />;
  }

  if (eta === null || eta > 120)
    return (
      <Eta
        tema={tema}
        etaMinima={etaMinima}
        onConferma={(d) => {
          salvaPreferenza(PREFERENZE.nascita, d);
          setNascita(d);
        }}
      />
    );
  if (eta < etaMinima) return <Schermata tema={tema.tema} testo={t("eta.troppo_giovane", { eta: etaMinima })} />;

  const indietro = () => (history.length > 1 ? history.back() : vai("/"));
  const codice = percorso.startsWith(PERCORSO_SFIDA) ? percorso.slice(PERCORSO_SFIDA.length).split("/")[0]! : null;
  const inPartita = codice !== null && CODICE_REGEX.test(codice);
  // Le richieste dal link personale arrivano su tutte le schermate tranne la partita.
  const richieste = accountAttivi && <Richieste attive={!inPartita} />;
  const conRichieste = (x: ReactNode) => (
    <>
      {richieste}
      {x}
    </>
  );

  // v037: «Riesci a non ridere?» — il video messo online (/v/<codice>)
  if (percorso.startsWith(PERCORSO_VIDEO)) {
    const codiceVideo = percorso.slice(PERCORSO_VIDEO.length).split("/")[0] ?? "";
    return <RiesciANonRidere codice={codiceVideo} tema={tema} onEsci={() => vai("/")} />;
  }
  if (percorso.startsWith(PERCORSO_PROFILO_PUBBLICO)) {
    const grezzo = percorso.slice(PERCORSO_PROFILO_PUBBLICO.length).split("/")[0] ?? "";
    let nome = grezzo;
    try {
      nome = decodeURIComponent(grezzo);
    } catch {
      // indirizzo malformato (es. /@%E0): il profilo risulterà inesistente, senza errori
    }
    return conRichieste(<ProfiloPubblico key={nome} nomeUtente={nome} tema={tema} onEsci={() => vai("/")} />);
  }
  if (accountAttivi && percorso === PERCORSO_PROVA) return conRichieste(<StrumentiProva />);
  if (percorso === PERCORSO_NEGOZIO) return conRichieste(<Negozio onIndietro={indietro} />);
  if (percorso === PERCORSO_CLASSIFICA) return conRichieste(<Classifica onIndietro={indietro} />);
  // v033: amici e «Disponibili ora»
  if (accountAttivi && percorso === PERCORSO_AMICI) return conRichieste(<Amici onIndietro={indietro} />);
  if (accountAttivi && percorso === PERCORSO_DISPONIBILI) return conRichieste(<Disponibili onIndietro={indietro} />);
  if (percorso === PERCORSO_AIUTO) return conRichieste(<Aiuto onIndietro={indietro} />);
  if (percorso === PERCORSO_AVATAR) return conRichieste(<SceltaAvatar onIndietro={indietro} />); // v040: anche qui arrivano le sfide

  if (accountAttivi && percorso === PERCORSO_LIBERATORIA)
    return (
      <Liberatoria
        tema={tema}
        onIndietro={indietro}
        onAccedi={() => {
          dopoAccesso(PERCORSO_LIBERATORIA);
          vai(PERCORSO_ACCESSO);
        }}
      />
    );

  if (accountAttivi && percorso === PERCORSO_ACCESSO)
    return <Accesso tema={tema} onFatto={() => vai(dopoAccesso() ?? PERCORSO_PROFILO, true)} onIndietro={() => history.length > 1 ? history.back() : vai("/")} />;
  if (accountAttivi && percorso === PERCORSO_PROFILO) return conRichieste(<Profilo onIndietro={() => vai("/")} onPiani={() => vai(PERCORSO_PIANI)} />);
  if (percorso === PERCORSO_PIANI) return conRichieste(<Piani onIndietro={() => (history.length > 1 ? history.back() : vai("/"))} onAccedi={() => vai(PERCORSO_ACCESSO)} />);

  if (codice !== null && inPartita) {
    // Partita di un torneo: uscendo si torna al tabellone.
    const torneo = torneoDellaPartita(codice);
    return (
      <>
        <AvvisoRete server={server} onRiprova={() => void caricaImpostazioni()} />
        <Gioco
          key={codice}
          codice={codice}
          tema={tema}
          onEsci={() => vai(torneo ? `${PERCORSO_TORNEO}${torneo}` : "/")}
          onNuovaSfida={() => vai(`/?${PARAMETRO_NUOVA}=1`)}
        />
      </>
    );
  }
  if (percorso.startsWith(PERCORSO_GRUPPO)) {
    const codiceGruppo = percorso.slice(PERCORSO_GRUPPO.length).split("/")[0] ?? "";
    if (CODICE_REGEX.test(codiceGruppo)) {
      return (
        <>
          <AvvisoRete server={server} onRiprova={() => void caricaImpostazioni()} />
          <Gruppo key={codiceGruppo} codice={codiceGruppo} tema={tema} />
        </>
      );
    }
  }
  if (percorso.startsWith(PERCORSO_TORNEO)) {
    const [codiceTorneo = "", sotto] = percorso.slice(PERCORSO_TORNEO.length).split("/");
    if (CODICE_REGEX.test(codiceTorneo)) {
      return (
        <>
          <AvvisoRete server={server} onRiprova={() => void caricaImpostazioni()} />
          {sotto === "guarda" ? <Guarda key={`g${codiceTorneo}`} codice={codiceTorneo} tema={tema} /> : conRichieste(<Torneo key={codiceTorneo} codice={codiceTorneo} tema={tema} />)}
        </>
      );
    }
  }
  const apriSfida = new URLSearchParams(window.location.search).has(PARAMETRO_NUOVA);

  return (
    <>
      <AvvisoRete server={server} onRiprova={() => void caricaImpostazioni()} />
      {richieste}
      <Home
        key={apriSfida ? "nuova" : "home"}
        apriSfida={apriSfida}
        giocoAttivo={giocoAttivo}
        tema={tema}
        temiDisponibili={temiVisibili}
        temiUsabili={temiUsabili}
        lingue={lingue}
        lingua={lingua}
        onLingua={(l) => void scegliLingua(l)}
        server={server}
        tempoReale={tempoReale}
      />
    </>
  );
}

/** Dove tornare dopo l'accesso (es. la liberatoria aperta da un link). Senza argomento: lo legge e lo cancella. */
function dopoAccesso(percorso?: string): string | null {
  try {
    if (percorso) {
      sessionStorage.setItem("ld.dopo_accesso", percorso);
      return percorso;
    }
    const p = sessionStorage.getItem("ld.dopo_accesso");
    sessionStorage.removeItem("ld.dopo_accesso");
    return p && p.startsWith("/") && !p.startsWith("//") ? p : null;
  } catch {
    return null;
  }
}
